import { useState } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import {
  BUSINESS_ENTITY_TYPES,
  CHARITABLE_ENTITY_TYPES,
  GRANTOR_ENTITY_TYPES,
} from '@/modules/entities/entities.constants';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';

export interface CreateTrustOrBusinessButtonProps {
  householdId: string;
  /** Called after either entity is created, so the pane can pick it up. */
  onAfterCreate: () => void;
}

/**
 * The empty state's "Create" menu.
 *
 * Deliberately not `CreateNewEntityOrStrategy`: that one offers a wider menu
 * and navigates to the Estate page after creating, which would throw the user
 * out of this wizard. Here the new entity should just appear as another row to
 * link.
 */
export function CreateTrustOrBusinessButton({
  householdId,
  onAfterCreate,
}: CreateTrustOrBusinessButtonProps) {
  const [isCreateTrustOpen, setIsCreateTrustOpen] = useState(false);
  const [isCreateBusinessOpen, setIsCreateBusinessOpen] = useState(false);

  const handleAfterCreate = (close: () => void) => () => {
    close();
    onAfterCreate();
  };

  return (
    <>
      {isCreateTrustOpen && (
        <CreateEntityShortFormModal
          isOpen
          onClose={() => setIsCreateTrustOpen(false)}
          householdId={householdId}
          limitEntityTypes={[
            ...GRANTOR_ENTITY_TYPES,
            ...CHARITABLE_ENTITY_TYPES,
          ]}
          heading="Create trust"
          entityTypeLabel="Trust type"
          buttonText="Create trust"
          onAfterCreate={handleAfterCreate(() => setIsCreateTrustOpen(false))}
        />
      )}
      {isCreateBusinessOpen && (
        <CreateEntityShortFormModal
          isOpen
          onClose={() => setIsCreateBusinessOpen(false)}
          householdId={householdId}
          limitEntityTypes={[...BUSINESS_ENTITY_TYPES]}
          heading="Create business"
          entityTypeLabel="Business type"
          buttonText="Create business"
          onAfterCreate={handleAfterCreate(() =>
            setIsCreateBusinessOpen(false)
          )}
        />
      )}

      <ButtonWithPopover
        label="Create"
        startIcon={PlusIcon}
        variant="secondary"
        size="sm"
        popperVariant="menuBelow"
      >
        <MenuItem
          key="createTrust"
          label="Create trust"
          onClick={() => setIsCreateTrustOpen(true)}
        />
        <MenuItem
          key="createBusiness"
          label="Create business"
          onClick={() => setIsCreateBusinessOpen(true)}
        />
      </ButtonWithPopover>
    </>
  );
}
